import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import React, { useContext, useMemo } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ServiceTitanAdjustableCapacityDTO, ST, Technician } from '_lib/api/apiTypes.shim';

import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { DialogContentTextOrMarkup } from '_lib/components/DialogContentText/DialogContentText';
import { Divider } from '_lib/components/Divider/Divider';
import { ExternalLinkOrDefault } from '_lib/components/ExternalLinkOrDefault/ExternalLinkOrDefault';
import { Grid } from '_lib/components/Grid/Grid';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemButton } from '_lib/components/ListItemButton/ListItemButton';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyLabels } from '_lib/constants/company.constants';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { formatPhone, getFullName } from '_lib/utils/helpers';

import type { Simplify } from 'type-fest';

type TechAssignDialogProps = Simplify<
  AsyncDialogProps<Technician> & {
    companyId: string;
    onCallText: string | null;
    contactScheduleUrl: string | null;
    capacityItem?: Pick<ServiceTitanAdjustableCapacityDTO, 'technicians'>;
  }
>;

const TechAssignDialog = (props: TechAssignDialogProps): React.ReactElement => {
  const { companyId, contactScheduleUrl, onCallText, capacityItem, onClose, ...dialogProps } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyId), [api, companyId]);
  const techniciansQuery = serviceTitanApi.technicians.useUnpagedListCached();

  const sortedTechnicians =
    techniciansQuery?.data
      ?.filter((tech) => {
        if (!capacityItem) return true;
        if (!tech.isManagedTech) return false;

        return capacityItem.technicians.some(
          (capTech) => capTech.id === tech.providerId && capTech.status === ST.CapacityTechnicianStatus.Available
        );
      })
      ?.sort((techA, techB) => (getFullName(techA) > getFullName(techB) ? 1 : -1)) || [];

  return (
    <DialogWrapper titleText='ON CALL TECHS' fullWidth maxWidth='xl' onClose={onClose} {...dialogProps}>
      <DialogContent sx={{ overflowY: 'visible' }}>
        <Stack>
          <HeaderBox headerText='Contact Schedule URL'>
            <ExternalLinkOrDefault url={contactScheduleUrl} label={CompanyLabels.CONTACT_SCHEDULE_URL} />
          </HeaderBox>
          <HeaderBox headerText='On Call Instructions'>
            <DialogContentTextOrMarkup
              text={onCallText || 'No On Call Instructions'}
              renderMarkup={!!onCallText}
              whiteSpace='pre-line'
            />
          </HeaderBox>
          <Divider orientation='horizontal' />
          <HeaderBox headerText='Techs'>
            <Typography text='Select a tech to assign' />
          </HeaderBox>
        </Stack>
      </DialogContent>
      <DialogContent>
        <List disablePadding>
          {sortedTechnicians.length > 0 &&
            sortedTechnicians.map((technician) => (
              <ListItem
                key={technician.providerId}
                divider
                secondaryAction={
                  <Tooltip title='Select Tech' placement='left'>
                    <IconButton
                      onClick={() => {
                        onClose({ action: 'CONFIRM', value: technician });
                      }}>
                      <AssignmentIndIcon />
                    </IconButton>
                  </Tooltip>
                }>
                <ListItemButton disableRipple disableTouchRipple>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={12}>
                      <Stack direction={'row'} spacing={2} sx={{ alignItems: 'self-end' }}>
                        <Typography text={getFullName(technician)} />
                        <PhoneInTalkIcon />
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Typography text={'Technician'} />
                    </Grid>
                    <Grid item xs={6} sm={12}>
                      <Typography
                        text={
                          technician?.phoneNumber ? formatPhone(technician?.phoneNumber) : 'No Phone Number for Tech'
                        }
                      />
                    </Grid>
                  </Grid>
                </ListItemButton>
              </ListItem>
            ))}
          {!sortedTechnicians.length && !techniciansQuery.isFetching && (
            <div>No Technicians Found for this Company</div>
          )}
        </List>
      </DialogContent>
    </DialogWrapper>
  );
};

type TechAssignDialog = AsyncDialog<Technician, TechAssignDialogProps>;

export const useTechAssignDialog = (): TechAssignDialog => useAsyncDialog(TechAssignDialog);
